/**
 * FERP Portal - Tenant Branding Admin Page
 *
 * Full CRUD management for tenant branding configuration: company identity,
 * colors, logos, domains, compliance frameworks, and feature flags.
 *
 * Architecture:
 *   - Backend: getTenantBranding query + createTenantBranding/updateTenantBranding mutations
 *   - Repository: TenantBrandingRepository in metadata_api
 *   - Config source: common/configs/seed/tenant-branding/*.yaml
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: all data from backend queries
 * - Admin view layer only
 */

import {
  Alert,
  Badge,
  Card,
  ColorSwatch,
  Container,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBrush,
  IconBuilding,
  IconCertificate,
  IconInfoCircle,
  IconPalette,
  IconWorld,
} from '@tabler/icons-react';
import { useGetTenantBrandingAdminQuery } from '@/generated/graphql';
import { useTenantId } from '@/auth';

export function TenantBrandingAdminPage() {
  const tenantId = useTenantId();
  const { data, loading, error } = useGetTenantBrandingAdminQuery({
    variables: { tenantId },
  });
  const branding = data?.getTenantBranding;

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Branding">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Tenant Branding</Title>
            <Text c="dimmed" size="sm">
              Company identity, colors, domains, and compliance framework configuration
            </Text>
          </div>
          {branding && (
            <Badge variant="light" color="blue" size="lg">
              {branding.industry}
            </Badge>
          )}
        </Group>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" title="About Tenant Branding">
          Branding configuration controls the portal appearance, public website data export
          (DRR-107 signed manifest), and compliance framework declarations. Changes take effect
          after re-seeding or via the updateTenantBranding mutation.
        </Alert>

        {loading && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}

        {!loading && !branding && (
          <Alert icon={<IconInfoCircle size={16} />} color="yellow" title="No Branding Configured">
            No branding configuration found for this tenant. Seed from tenant-branding YAML configs.
          </Alert>
        )}

        {branding && (
          <>
            {/* Company Identity */}
            <Card withBorder p="lg">
              <Group gap="sm" mb="md">
                <ThemeIcon variant="light" color="blue" size="lg">
                  <IconBuilding size={20} />
                </ThemeIcon>
                <div>
                  <Text fw={700} size="lg">{branding.companyName}</Text>
                  {branding.companyTagline && (
                    <Text c="dimmed" size="sm">{branding.companyTagline}</Text>
                  )}
                </div>
              </Group>

              <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
                <Paper withBorder p="sm">
                  <Text size="xs" c="dimmed" fw={500} mb={4}>Industry</Text>
                  <Text size="sm" fw={500}>{branding.industry}</Text>
                </Paper>
                {branding.licenseType && (
                  <Paper withBorder p="sm">
                    <Text size="xs" c="dimmed" fw={500} mb={4}>License Type</Text>
                    <Text size="sm" fw={500}>{branding.licenseType}</Text>
                  </Paper>
                )}
                {branding.address && (
                  <Paper withBorder p="sm">
                    <Text size="xs" c="dimmed" fw={500} mb={4}>Address</Text>
                    <Text size="sm">{branding.address}</Text>
                  </Paper>
                )}
                {branding.hours && (
                  <Paper withBorder p="sm">
                    <Text size="xs" c="dimmed" fw={500} mb={4}>Hours</Text>
                    <Text size="sm">{branding.hours}</Text>
                  </Paper>
                )}
              </SimpleGrid>
            </Card>

            {/* Color Palette */}
            <Card withBorder p="md">
              <Group gap="sm" mb="sm">
                <ThemeIcon variant="light" color="grape" size="sm">
                  <IconPalette size={14} />
                </ThemeIcon>
                <Text fw={600}>Color Palette</Text>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 4 }} spacing="md">
                <Group gap="sm">
                  <ColorSwatch color={branding.primaryColor} size={24} />
                  <div>
                    <Text size="xs" c="dimmed">Primary</Text>
                    <Text size="sm" ff="monospace">{branding.primaryColor}</Text>
                  </div>
                </Group>
                <Group gap="sm">
                  <ColorSwatch color={branding.secondaryColor} size={24} />
                  <div>
                    <Text size="xs" c="dimmed">Secondary</Text>
                    <Text size="sm" ff="monospace">{branding.secondaryColor}</Text>
                  </div>
                </Group>
                <Group gap="sm">
                  <ColorSwatch color={branding.accentColor} size={24} />
                  <div>
                    <Text size="xs" c="dimmed">Accent</Text>
                    <Text size="sm" ff="monospace">{branding.accentColor}</Text>
                  </div>
                </Group>
                <div>
                  <Text size="xs" c="dimmed">Font Family</Text>
                  <Text size="sm">{branding.fontFamily}</Text>
                </div>
              </SimpleGrid>
            </Card>

            {/* Domains */}
            {(branding.subdomain || branding.customDomain) && (
              <Card withBorder p="md">
                <Group gap="sm" mb="sm">
                  <ThemeIcon variant="light" color="teal" size="sm">
                    <IconWorld size={14} />
                  </ThemeIcon>
                  <Text fw={600}>Domains</Text>
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  {branding.subdomain && (
                    <div>
                      <Text size="xs" c="dimmed">Subdomain</Text>
                      <Text size="sm" ff="monospace">{branding.subdomain}.ferp.app</Text>
                    </div>
                  )}
                  {branding.customDomain && (
                    <div>
                      <Text size="xs" c="dimmed">Custom Domain</Text>
                      <Text size="sm" ff="monospace">{branding.customDomain}</Text>
                    </div>
                  )}
                </SimpleGrid>
              </Card>
            )}

            {/* Compliance & Accreditations */}
            <Card withBorder p="md">
              <Group gap="sm" mb="sm">
                <ThemeIcon variant="light" color="red" size="sm">
                  <IconCertificate size={14} />
                </ThemeIcon>
                <Text fw={600}>Compliance & Accreditations</Text>
              </Group>
              <Stack gap="sm">
                {branding.complianceFrameworks.length > 0 && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Compliance Frameworks</Text>
                    <Group gap="xs">
                      {branding.complianceFrameworks.map((f) => (
                        <Badge key={f} variant="light" color="red" size="sm">{f}</Badge>
                      ))}
                    </Group>
                  </div>
                )}
                {branding.accreditations.length > 0 && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Accreditations</Text>
                    <Group gap="xs">
                      {branding.accreditations.map((a) => (
                        <Badge key={a} variant="outline" color="orange" size="sm">{a}</Badge>
                      ))}
                    </Group>
                  </div>
                )}
                {branding.services.length > 0 && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Services</Text>
                    <Group gap="xs">
                      {branding.services.map((s) => (
                        <Badge key={s} variant="outline" color="gray" size="sm">{s}</Badge>
                      ))}
                    </Group>
                  </div>
                )}
              </Stack>
            </Card>

            {/* Visual Assets */}
            <Card withBorder p="md">
              <Group gap="sm" mb="sm">
                <ThemeIcon variant="light" color="indigo" size="sm">
                  <IconBrush size={14} />
                </ThemeIcon>
                <Text fw={600}>Visual Assets</Text>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                <div>
                  <Text size="xs" c="dimmed">Logo URL</Text>
                  <Text size="xs" ff="monospace" lineClamp={1}>{branding.logoUrl ?? 'Not set'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Favicon URL</Text>
                  <Text size="xs" ff="monospace" lineClamp={1}>{branding.faviconUrl ?? 'Not set'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Hide FERP Branding</Text>
                  <Badge variant="outline" size="xs" color={branding.hideFerpBranding ? 'green' : 'gray'}>
                    {branding.hideFerpBranding ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </SimpleGrid>
            </Card>

            {/* Audit */}
            <Text size="xs" c="dimmed">
              Created: {branding.createdAt}
              {branding.updatedAt && ` · Updated: ${branding.updatedAt}`}
            </Text>
          </>
        )}
      </Stack>
    </Container>
  );
}

export default TenantBrandingAdminPage;
